import {useState} from 'react'
import {useResizeObserver} from '@github-ui/use-resize-observer'

/**
 * useFlexWrapHysteresis
 * Detects if flex children are wrapped and applies hysteresis to avoid rapid toggling at breakpoints.
 * @param UNWRAP_BUFFER - px buffer before allowing unwrapping
 * @returns { ref, isWrapped }
 */
export function useFlexWrapHysteresis(UNWRAP_BUFFER = 40) {
  const [isWrapped, setIsWrapped] = useState(false)
  const [wrapThreshold, setWrapThreshold] = useState<number | null>(null)

  // Shared rAF-coalesced observer. The handler reads layout (offsetTop/Width)
  // inside the observer's rAF flush, which is post-layout, and the hook always
  // invokes the latest handler so `wrapThreshold` is never stale.
  const flexRef = useResizeObserver(entry => {
    const container = entry.target as HTMLElement
    const children = Array.from(container.children)
    if (children.length < 2) {
      setIsWrapped(false)
      setWrapThreshold(null)
      return
    }
    const firstTop = (children[0] as HTMLElement).offsetTop
    const wrapped = children.some(child => (child as HTMLElement).offsetTop !== firstTop)

    setIsWrapped(prev => {
      if (prev !== wrapped) return wrapped
      return prev
    })

    if (wrapped) {
      if (!wrapThreshold || container.offsetWidth < wrapThreshold) setWrapThreshold(container.offsetWidth)
    } else {
      if (wrapThreshold && container.offsetWidth > wrapThreshold + UNWRAP_BUFFER) {
        setWrapThreshold(null)
      }
    }
  })

  return {flexRef, isWrapped}
}
